import { calendarApi } from "./client";
import type {
  BusyBlock,
  Calendar,
  CalendarsResponse,
  CalendarEvent,
  CalendarUserMe,
  CancelScope,
  CancellationStatus,
  EventScope,
  Channel,
  CreateCalendarRequest,
  CreateChannelRequest,
  CreateEventRequest,
  EventDeliveries,
  EventProposal,
  EventRsvpState,
  EventsResponse,
  InvitationResponse,
  InviteOccurrences,
  ResourcesResponse,
  TaskStatusResponse,
  UpdateCalendarRequest,
} from "./types/calendar";
import type { ListParams } from "./types/common";

/** What else an RSVP may carry (design "Planner Invite States" 2.4 / 2.6). */
export interface RespondOptions {
  /** A note to the organiser; an empty string clears an earlier one. */
  note?: string;
  scope?: EventScope;
  /** The occurrence a scoped answer starts from (ISO). */
  occurrence?: string;
}

function listParamsToSearch(params?: object): string {
  if (!params) return "";
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== "") p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : "";
}

export const calendarService = {
  getMe: async (): Promise<CalendarUserMe> => {
    return calendarApi.get("/users/me/");
  },

  /**
   * Patch the calendar-service user. `IsSelf` means this only ever succeeds for
   * the caller's own id. Note this is the *calendar* service's User row, which is
   * a different table from the people service's — they hold independent
   * `timezone` columns (#1585 D5b touches the calendar one only).
   */
  updateUser: async (userId: string, patch: Partial<{ timezone: string }>): Promise<CalendarUserMe> => {
    return calendarApi.patch(`/users/${userId}/`, patch);
  },

  getCalendars: async (params?: ListParams): Promise<CalendarsResponse> => {
    return calendarApi.get(`/calendars/${listParamsToSearch(params)}`);
  },

  getCalendar: async (calendarId: string): Promise<Calendar> => {
    return calendarApi.get(`/calendars/${calendarId}/`);
  },

  createCalendar: async (request: CreateCalendarRequest): Promise<Calendar> => {
    return calendarApi.post("/calendars/", request);
  },

  updateCalendar: async (calendarId: string, patch: UpdateCalendarRequest): Promise<Calendar> => {
    return calendarApi.patch(`/calendars/${calendarId}/`, patch);
  },

  deleteCalendar: async (calendarId: string): Promise<void> => {
    return calendarApi.delete(`/calendars/${calendarId}/`);
  },

  getEvents: async (
    calendarId: string,
    params?: ListParams & { start?: string; end?: string }
  ): Promise<EventsResponse> => {
    return calendarApi.get(`/calendars/${calendarId}/events/${listParamsToSearch(params)}`);
  },

  getEvent: async (calendarId: string, eventId: string): Promise<CalendarEvent> => {
    const eid = encodeURIComponent(eventId);
    return calendarApi.get(`/calendars/${calendarId}/events/${eid}/`);
  },

  /**
   * Cross-calendar substring search over events within ±60 days of today.
   * Backend: GET /api/v1.0/calendars/events/search/?q=<q>&page_size=<n>
   */
  searchEvents: async (
    query: string,
    pageSize = 8,
  ): Promise<EventsResponse> => {
    return calendarApi.get(
      `/calendars/events/search/${listParamsToSearch({ q: query, page_size: pageSize })}`,
    );
  },

  /**
   * Events the caller was invited to but owns no copy of — GH-1608.
   * Backend: GET /api/v1.0/calendars/events/invited/?start=<iso>&end=<iso>
   *
   * Sibling of `searchEvents`: a collection action on the calendars viewset,
   * not scoped to any one calendar, because by definition the caller has
   * access to none of the calendars these events live on. Every row comes
   * back with `calendar_id: null` and `is_invitation: true`.
   */
  getInvitedEvents: async (params: {
    start: string;
    end: string;
  }): Promise<EventsResponse> => {
    return calendarApi.get(`/calendars/events/invited/${listParamsToSearch(params)}`);
  },

  /**
   * Respond to an invitation from inside the app — GH-1608 Pass 5.
   * Backend: POST /api/v1.0/events/{uid}/rsvp/ {"status": ...}
   *
   * Not scoped to a calendar: the invitee owns no copy of the event, so there
   * is no calendar id to address it by. The backend resolves the caller
   * through the attendee index, which is also the authorisation answer.
   *
   * Lower-cased on the way out and upper-cased on the way back because the two
   * sides genuinely differ: the endpoint accepts the lower-case status names
   * shared with the email-link flow, while PARTSTAT is upper-case per RFC 5545
   * and that is what every read returns as `my_response`.
   */
  respondToEvent: async (
    eventUid: string,
    response: Exclude<InvitationResponse, "NEEDS-ACTION">,
    options?: RespondOptions
  ): Promise<{ status: string; event_uid: string }> => {
    const uid = encodeURIComponent(eventUid);
    const body: Record<string, string> = { status: response.toLowerCase() };
    // Design 2.4: a note rides with the answer; absent leaves any earlier one.
    if (options?.note !== undefined) body.note = options.note;
    // Design 2.6: on a repeating event, which occurrences the answer covers.
    if (options?.scope && options.scope !== "all") {
      body.scope = options.scope;
      if (options.occurrence) body.occurrence = options.occurrence;
    }
    return calendarApi.post(`/events/${uid}/rsvp/`, body);
  },

  /* Design "Planner Invite States" — guest and organiser actions. */

  /** An assignee marks their part of a task done (6.6). */
  markEventDone: async (eventUid: string, done: boolean): Promise<{ done: boolean }> =>
    calendarApi.post(`/events/${encodeURIComponent(eventUid)}/done/`, { done }),

  /** A guest adds guests, when the organiser allows it (6.3). */
  addGuestsAsGuest: async (
    eventUid: string,
    emails: string[]
  ): Promise<{ added: string[]; event?: CalendarEvent }> =>
    calendarApi.post(`/events/${encodeURIComponent(eventUid)}/guests/`, { emails }),

  /** A guest edits the event, when the organiser allows it (6.2). */
  editEventAsGuest: async (
    eventUid: string,
    patch: Partial<Pick<CalendarEvent, "title" | "description" | "location" | "start" | "end" | "all_day" | "video_link">>
  ): Promise<CalendarEvent> =>
    calendarApi.patch(`/events/${encodeURIComponent(eventUid)}/as-guest/`, patch),

  /** Proposals on an event: all open ones for the organiser, your own for a guest. */
  getEventProposals: async (
    eventUid: string
  ): Promise<{ role: "organizer" | "guest"; proposals: EventProposal[] }> =>
    calendarApi.get(`/events/${encodeURIComponent(eventUid)}/proposals/`),

  /** A guest proposes a new time (3.1). */
  proposeEventTime: async (
    eventUid: string,
    body: { start: string; end: string; note?: string; occurrence?: string }
  ): Promise<EventProposal> =>
    calendarApi.post(`/events/${encodeURIComponent(eventUid)}/proposals/`, body),

  /** Busy times of the organiser and same-org guests in a window (invitee only, max 14 days). */
  getProposalBusy: async (eventUid: string, from: string, to: string): Promise<{ busy: BusyBlock[] }> => {
    // Normalise to UTC `Z` and encode: a raw `+00:00` would reach the server as a space (400).
    const qs = new URLSearchParams({ from: new Date(from).toISOString(), to: new Date(to).toISOString() });
    return calendarApi.get(`/events/${encodeURIComponent(eventUid)}/proposals/busy/?${qs.toString()}`);
  },

  /** The proposer withdraws (3.1). */
  withdrawProposal: async (eventUid: string, proposalId: string): Promise<void> =>
    calendarApi.delete(`/events/${encodeURIComponent(eventUid)}/proposals/${proposalId}/`),

  /** The organiser accepts a proposal: the event moves (3.4). */
  acceptProposal: async (
    eventUid: string,
    proposalId: string
  ): Promise<{ proposal: EventProposal; event: CalendarEvent }> =>
    calendarApi.post(`/events/${encodeURIComponent(eventUid)}/proposals/${proposalId}/accept/`, {}),

  /** The organiser keeps the time, optionally saying why (3.5). */
  declineProposal: async (eventUid: string, proposalId: string, note?: string): Promise<EventProposal> =>
    calendarApi.post(`/events/${encodeURIComponent(eventUid)}/proposals/${proposalId}/decline/`, {
      ...(note ? { note } : {}),
    }),

  /** GH-2202 G: each guest's delivery status — organiser only (404 otherwise). */
  getEventDeliveries: async (eventUid: string): Promise<EventDeliveries> =>
    calendarApi.get(`/events/${encodeURIComponent(eventUid)}/deliveries/`),

  /** GH-2202 G2: send the event as it is now to one guest whose email failed or bounced. */
  resendEventDelivery: async (eventUid: string, email: string): Promise<{ queued: boolean }> =>
    calendarApi.post(`/events/${encodeURIComponent(eventUid)}/deliveries/resend/`, { email }),

  /** The organiser hands the event to someone else (4.8). */
  transferEvent: async (eventUid: string, email: string): Promise<{ organizer: string }> =>
    calendarApi.post(`/events/${encodeURIComponent(eventUid)}/transfer/`, { email }),

  /**
   * The caller's current answer to an invitation — GH-1871 D2.
   * Backend: GET /api/v1.0/events/{uid}/rsvp/
   *
   * Always 200. `can_respond` is false with a `reason` when the caller is the
   * organizer, the event is cancelled, or they are not on the guest list (an
   * unknown UID reads as `not_invitee` too, so this says no more than the
   * POST's 404 does).
   */
  getEventRsvp: async (eventUid: string): Promise<EventRsvpState> => {
    const uid = encodeURIComponent(eventUid);
    return calendarApi.get(`/events/${uid}/rsvp/`);
  },

  /**
   * Answer an invite from an organiser outside We-Co (GH-2198). The invite has
   * no We-Co copy (`getEventRsvp` says `not_invitee`), so the raw `.ics` goes
   * along: the first answer imports it into the caller's calendar, a later one
   * answers that copy. Same answer shape as `respondToEvent`.
   * Backend: POST /api/v1.0/invites/answer/
   */
  answerOutsideInvite: async (
    ics: string,
    response: Exclude<InvitationResponse, "NEEDS-ACTION">,
    options?: RespondOptions
  ): Promise<{ status: string; event_uid: string; imported: boolean }> => {
    const body: Record<string, string> = { ics, status: response.toLowerCase() };
    if (options?.note !== undefined) body.note = options.note;
    if (options?.scope && options.scope !== "all") {
      body.scope = options.scope;
      if (options.occurrence) body.occurrence = options.occurrence;
    }
    return calendarApi.post("/invites/answer/", body);
  },

  /**
   * The upcoming occurrences of a repeating invite, for a "This event" /
   * "This and following" answer from the mail card (GH-2198). Reads the posted
   * `.ics` only; nothing is stored.
   * Backend: POST /api/v1.0/invites/occurrences/
   */
  getInviteOccurrences: async (ics: string): Promise<InviteOccurrences> =>
    calendarApi.post("/invites/occurrences/", { ics }),

  /**
   * Hide one event from the caller's own calendar — GH-1608 Pass 5.
   * Backend: POST /api/v1.0/events/{uid}/dismiss/
   *
   * Per-person: it never changes what anyone else sees (AC21). The backend
   * refuses with 409 unless the event is cancelled or the caller has declined
   * it (decision 16) — the UI hides the control in those cases (decision 40),
   * so a 409 here means the two have drifted apart, not that the user did
   * something wrong.
   */
  dismissEvent: async (
    eventUid: string
  ): Promise<{ dismissed: boolean; event_uid: string }> => {
    const uid = encodeURIComponent(eventUid);
    return calendarApi.post(`/events/${uid}/dismiss/`, {});
  },

  /**
   * Delete the caller's imported copy of an outside invite (GH-2198 AC34): the
   * organiser is sent a Decline for the whole series, then the copy goes.
   * Backend: DELETE /api/v1.0/events/{uid}/imported/
   */
  removeImportedEvent: async (eventUid: string): Promise<void> => {
    return calendarApi.delete(`/events/${encodeURIComponent(eventUid)}/imported/`);
  },

  createEvent: async (request: CreateEventRequest): Promise<CalendarEvent> => {
    const { calendar_id, ...body } = request;
    return calendarApi.post(`/calendars/${calendar_id}/events/`, body);
  },

  /**
   * GH-1610 D. `scope` and `occurrence` ride on the query string, not in the
   * PATCH body, so the discriminator is spelled identically here and on
   * `deleteEvent` — the server reads both through one validator.
   *
   * Both are optional, so omitting them sends no parameters and the server
   * defaults `scope` to `"all"`: every pre-#1610 caller is unchanged.
   *
   * The response may carry a DIFFERENT `id` than the one asked for. Under
   * `"following"` the server splits the series and the later half — the half
   * the edit landed on — takes a new UID, because RFC 4791 §5.3.2 forbids two
   * objects sharing a UID in one collection. Callers must read the identity
   * back off the response rather than assume it.
   */
  updateEvent: async (
    calendarId: string,
    eventId: string,
    updates: Partial<Omit<CreateEventRequest, "calendar_id">>,
    options?: { scope?: EventScope; occurrence?: string }
  ): Promise<CalendarEvent> => {
    const eid = encodeURIComponent(eventId);
    const query = listParamsToSearch(
      options?.scope ? { scope: options.scope, occurrence: options.occurrence } : undefined
    );
    return calendarApi.patch(`/calendars/${calendarId}/events/${eid}/${query}`, updates);
  },

  moveEvent: async (
    calendarId: string,
    eventId: string,
    targetCalendarId: string
  ): Promise<{ id: string; calendar_id: string }> => {
    const eid = encodeURIComponent(eventId);
    return calendarApi.post(`/calendars/${calendarId}/events/${eid}/move/`, {
      target_calendar_id: targetCalendarId,
    });
  },

  /**
   * GH-1610 C. `scope` and `occurrence` ride on the query string because the
   * route is UID-keyed and a DELETE carries no body.
   *
   * Both are optional so the signature stays backward-compatible: omitting them
   * sends no parameters at all, and the server defaults `scope` to `"all"` —
   * the behaviour every pre-#1610 caller already relies on.
   *
   * `occurrence` is the ISO start of the instance the user acted on, and the
   * server *requires* it for `"this"` and `"following"` rather than guessing:
   * a scoped cancellation aimed at the wrong instance removes the wrong date
   * and the organiser has no way to tell.
   *
   * GH-1932 C. Cancelling a whole event with guests answers 202 with a
   * cancellation to poll (`getCancellation`): the server emails everyone first
   * and deletes only once every email went. Any other delete answers 204 (`{}`).
   */
  deleteEvent: async (
    calendarId: string,
    eventId: string,
    options?: { scope?: CancelScope; occurrence?: string }
  ): Promise<Partial<CancellationStatus>> => {
    const eid = encodeURIComponent(eventId);
    const query = listParamsToSearch(
      options?.scope ? { scope: options.scope, occurrence: options.occurrence } : undefined
    );
    return calendarApi.delete(`/calendars/${calendarId}/events/${eid}/${query}`);
  },

  getCancellation: async (cancellationId: string): Promise<CancellationStatus> => {
    return calendarApi.get(`/calendars/cancellations/${cancellationId}/`);
  },

  getResources: async (params?: ListParams): Promise<ResourcesResponse> => {
    return calendarApi.get(`/resources/${listParamsToSearch(params)}`);
  },

  getOrganizationSettings: async (): Promise<OrganizationSettingsResponse> => {
    return calendarApi.get("/organization-settings/current/");
  },

  updateOrganizationSettings: async (
    body: Partial<{ default_sharing_level: string }>
  ): Promise<OrganizationSettingsResponse> => {
    return calendarApi.patch("/organization-settings/current/", body);
  },

  importEvents: async (caldavPath: string, file: File): Promise<{ task_id: string }> => {
    const fd = new FormData();
    fd.append("caldav_path", caldavPath);
    fd.append("file", file);
    return calendarApi.upload("/calendars/import-events/", fd);
  },

  // Async (Dramatiq) job-status poller — renamed off `/tasks/` to free that
  // prefix for the real Task resource introduced in #1307.
  getJobStatus: async (taskId: string): Promise<TaskStatusResponse> => {
    return calendarApi.get(`/jobs/${taskId}/`);
  },

  getChannels: async (type?: string): Promise<Channel[]> => {
    const suffix = type ? `?type=${encodeURIComponent(type)}` : "";
    return calendarApi.get(`/channels/${suffix}`);
  },

  createChannel: async (body: CreateChannelRequest): Promise<Channel> => {
    return calendarApi.post("/channels/", body);
  },

  deleteChannel: async (channelId: string): Promise<void> => {
    return calendarApi.delete(`/channels/${channelId}/`);
  },

  regenerateChannelToken: async (channelId: string): Promise<Channel> => {
    return calendarApi.post(`/channels/${channelId}/regenerate-token/`, {});
  },
};

export interface OrganizationSettingsResponse {
  id: string;
  name: string;
  sharing_level: string;
}
